import {
  createSession,
  deleteAbandonedCardOrders,
  getCustomerId,
  getSessionStatus,
  makePayment,
  reconcileCardOrder,
} from "api/firebase";
import { Currency, PaidFeatureEnum } from "@evennt-tech/api-v2-core";
import PaymentMethod, {
  CardPayment,
  IdealPayment,
  PaymentMethodType,
} from "ts/api/paymentMethod";
import Transfer from "ts/api/transfer";
import { PriceNames } from "api/stripe/priceNames";

export type CardPaymentResponse = {
  type: PaymentMethodType.cardPayment;
  clientSecret: string;
  paymentIntentId: string;
};

export type IdealPaymentResponse = Omit<CardPaymentResponse, "type"> & {
  type: PaymentMethodType.idealPayment;
};
export type BankTransferResponse = {
  type: PaymentMethodType.bankTransfer;
  invoiceId: string;
  invoiceNumber: string | null;
};
export type GetConnectedAccountIdParams = {
  code: string;
  currency: Currency;
};
export type GetCustomerIdParams = {
  email: string;
  currency: Currency;
};
export type ReconcileCardOrderResult =
  | { outcome: "paid" }
  | { outcome: "awaitingPayment" }
  | {
      outcome: "requiresAction";
      clientSecret: string;
      cardLast4?: string;
      cardBrand?: string;
    }
  | { outcome: "skipped"; reason: string };
export type MakePaymentResponse<T> = T extends CardPayment
  ? CardPaymentResponse
  : T extends IdealPayment
  ? IdealPaymentResponse
  : BankTransferResponse;

const STRIPE_AMOUNT_MULTIPLIER = 100;

const getStripeAmount = (amount: number) =>
  Math.round(amount * STRIPE_AMOUNT_MULTIPLIER);

const mapTransfer = (transfer: Transfer) => ({
  ...transfer,
  metadata: {
    id: transfer.metadata.id,
    role: transfer.metadata.role,
  },
  amount: getStripeAmount(transfer.amount),
});
const stripeService = {
  getCustomerId: async ({
    email,
    currency,
  }: GetCustomerIdParams): Promise<string | null> => {
    try {
      const { data } = await getCustomerId()({
        currency,
        email,
      });

      return data.customerId;
    } catch (e) {
      console.error(e);
      return null;
    }
  },
  makePayment: async <T extends PaymentMethod>({
    amount,
    ...params
  }: T): Promise<MakePaymentResponse<T>> => {
    const { data } = await makePayment()({
      amount: getStripeAmount(amount),
      ...params,
    });

    return data;
  },
  createSession: ({
    priceName,
    showId,
    organiserId,
    oldPriceName,
  }: {
    priceName: PriceNames | string;
    showId: string;
    organiserId: string;
    oldPriceName?: string;
  }) => {
    return createSession()({
      priceName,
      showId,
      organiserId,
      oldPriceName,
      returnOrigin: window.location.origin,
    });
  },
  getSessionStatus: ({
    sessionId,
    enablePaidFeatureOnComplete = null,
  }: {
    sessionId: string;
    enablePaidFeatureOnComplete?: {
      name: PaidFeatureEnum | string;
      showId: string;
    } | null;
  }) => {
    return getSessionStatus()({ sessionId, enablePaidFeatureOnComplete });
  },
  reconcileCardOrder: async ({
    showId,
    orderId,
  }: {
    showId: string;
    orderId: string;
  }): Promise<ReconcileCardOrderResult> => {
    const { data } = await reconcileCardOrder()({ showId, orderId });

    return data;
  },
  deleteAbandonedCardOrders: async ({
    showId,
    exhibitorPairId,
  }: {
    showId: string;
    exhibitorPairId: string;
  }): Promise<{ deletedOrderIds: string[] }> => {
    const { data } = await deleteAbandonedCardOrders()({
      showId,
      exhibitorPairId,
    });

    return data;
  },
};

export default stripeService;
